import { AlertTriangle } from "lucide-react";
import type {
  BuyerSmartImportCountsDto,
  BuyerSmartImportPreviewDto,
} from "@/api/buyerSmartImport";
import { StatCard, StatsBar } from "@/design-system/components/ui";
import { formatNumber } from "@/design-system/lib/format";
import { smartImportCopy } from "./smartImportCopy";

/**
 * What this import will do, in numbers (D4).
 *
 * <p>🔴 <b>The five outcome counts are an INVARIANT, not a summary</b>: rows
 * read = created + merged + already imported + before cutoff + rejected. Every
 * row lands in exactly one, and a preview whose five do not reconcile to the
 * sum is describing two different imports. It is rendered as a sentence rather
 * than left implicit, and a preview that fails it says so on screen — a number
 * nobody can check is a number nobody should act on.</p>
 */

export function countsReconcile(counts: BuyerSmartImportCountsDto): boolean {
  return (
    counts.rowsRead ===
    counts.created +
      counts.merged +
      counts.alreadyImported +
      counts.beforeCutoff +
      counts.rejected
  );
}

export interface SmartImportCountsProps {
  preview: BuyerSmartImportPreviewDto;
}

export function SmartImportCounts({ preview }: SmartImportCountsProps) {
  const c = preview.counts;
  const reconciles = countsReconcile(c);

  return (
    <section aria-label={smartImportCopy.countsTitle} data-testid="smart-import-counts">
      <h3 className="font-display font-semibold text-label uppercase tracking-label text-gold-shadow mb-space-2">
        {smartImportCopy.countsTitle}
      </h3>

      <StatsBar columns={6} data-testid="smart-import-outcome-counts">
        <StatCard
          label={smartImportCopy.rowsRead}
          value={formatNumber(c.rowsRead)}
          data-testid="count-rows-read"
        />
        <StatCard
          label={smartImportCopy.created}
          value={formatNumber(c.created)}
          data-testid="count-created"
        />
        <StatCard
          label={smartImportCopy.merged}
          value={formatNumber(c.merged)}
          data-testid="count-merged"
        />
        <StatCard
          label={smartImportCopy.alreadyImported}
          value={formatNumber(c.alreadyImported)}
          data-testid="count-already-imported"
        />
        <StatCard
          label={smartImportCopy.beforeCutoff}
          value={formatNumber(c.beforeCutoff)}
          data-testid="count-before-cutoff"
        />
        <StatCard
          label={smartImportCopy.rejected}
          value={formatNumber(c.rejected)}
          data-testid="count-rejected"
        />
      </StatsBar>

      <p
        className={
          "text-small " + (reconciles ? "text-gray-mid" : "text-status-error font-medium")
        }
        data-testid="smart-import-invariant"
      >
        {reconciles ? (
          `Every one of the ${formatNumber(c.rowsRead)} rows read is counted exactly once above.`
        ) : (
          <>
            <AlertTriangle
              size={14}
              strokeWidth={1.5}
              aria-hidden
              className="inline-block align-text-bottom mr-space-1"
            />
            These counts do not add up to the rows read. Do not import — report it.
          </>
        )}
      </p>

      <div className="mt-space-3 grid gap-space-2 text-small">
        <CountLine
          testId="count-duplicates-existing"
          label={smartImportCopy.duplicatesExisting}
          value={preview.duplicatesOfExistingBuyers}
        />
        <CountLine
          testId="count-duplicates-within-file"
          label={smartImportCopy.duplicatesWithinFile}
          value={preview.duplicatesWithinFile}
        />
        <CountLine
          testId="count-values-cleared"
          label={smartImportCopy.valuesCleared}
          value={preview.valuesCleared}
          hint={smartImportCopy.valuesClearedHint}
        />
        <CountLine
          testId="count-sentinels-found"
          label={smartImportCopy.sentinelsFound}
          value={preview.sentinelsFound}
          hint={smartImportCopy.sentinelsHint2}
        />
        <CountLine
          testId="count-dates-unreadable"
          label={smartImportCopy.datesUnreadable}
          value={preview.datesUnreadable}
        />
      </div>

      {preview.warnings.length > 0 && (
        <ul
          className="mt-space-3 grid gap-space-1 text-small text-status-warning-ink"
          data-testid="smart-import-preview-warnings"
        >
          {preview.warnings.map((w, i) => (
            <li key={`warning-${i}`} className="flex items-start gap-space-2">
              <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CountLine({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: number;
  hint?: string;
  testId: string;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-space-2" data-testid={testId}>
      <span className="font-medium text-onyx">{formatNumber(value)}</span>
      <span className="text-gray-mid">{label}</span>
      {hint && <span className="text-caption text-gray-mid">{hint}</span>}
    </div>
  );
}
